import * as React from "react";
import { connect } from "common/connect";
import StoreKeys from "common/storeKeys";
import {
    BountyModel,
    bountyModelContext,
    fillBountyFreeformParts,
} from "common/helpers/bountyHelper";
import BountyBracket from "./BountyBracket";
import Store from "common/store";
import ModalHelper from "common/helpers/modalHelper";
import BountyPickModal from "common/components/BountyPickModal/BountyPickModal";
import EventHelper from "common/helpers/eventHelper/eventHelper";
import UserManager from "common/userManager";
import AccountRequired from "common/components/Modals/AccountRequired/AccountRequired";
import NameplateButton from "common/components/NameplateButton";
import { GetCurrentGameId } from "common/constants/Games";
import { BracketActionRow, useBracketSurface } from "./BracketSurface";

const propKeys = {
    eventData: StoreKeys.EVENT.EVENTDATA,
    userBracketPicks: StoreKeys.EVENT.USER.BRACKET_PICKS,
    officialBracketPicks: StoreKeys.EVENT.OFFICIAL.BRACKET_PICKS,
    format: StoreKeys.EVENT.FORMAT,
    stageIndex: StoreKeys.EVENT.STAGE_INDEX,
    games: StoreKeys.GAMES,
};

function BountyBracketEditorC({
    eventData,
    userBracketPicks,
    officialBracketPicks,
    format,
    stageIndex,
    games,
}) {
    const {
        ready,
        width: viewerWidth,
        svgWrapper,
        surfaceProps,
    } = useBracketSurface({ spaced: true });

    const currentStage = format.stages[stageIndex];
    const currentEventStage = eventData.eventStages[stageIndex];

    const modelContext = () =>
        bountyModelContext(
            eventData,
            currentStage,
            currentEventStage,
            officialBracketPicks,
            Store.get(StoreKeys.EVENT.USER.BRACKET_PICKS)
        );

    const model = new BountyModel({
        ...modelContext(),
        userPicks: userBracketPicks ?? [],
    });
    const parts = fillBountyFreeformParts(model, eventData.teams, {});

    const gameId = GetCurrentGameId();
    const currentGame = games.find((g) => g.id === gameId);

    // Writes go through a fresh model so stale downstream picks are pruned.
    const applyMutation = (mutate) => {
        const freshModel = new BountyModel(modelContext());
        mutate(freshModel);
        const allPicks = Store.get(StoreKeys.EVENT.USER.BRACKET_PICKS) ?? [];
        const otherStagePicks = allPicks.filter(
            (p) => p.eventStageId !== currentEventStage.id
        );
        Store.set(StoreKeys.EVENT.USER.BRACKET_PICKS, [
            ...otherStagePicks,
            ...freshModel.serializePicks(),
        ]);
    };

    const clickParty = (e, match, selectedTop) => {
        if (!UserManager.isLoggedIn()) {
            ModalHelper.openModal(<AccountRequired />);
            return;
        }

        const round = model.partIds.indexOf(match.part);
        if (round < 0) {
            return;
        }
        const game = model.gamesForRound(round)[match.colIndex];
        if (!game || game.topTeamId == null) {
            return;
        }

        const teams = eventData.teams;
        const bestOf = model.bestOfPerRound[round] ?? 1;
        const topTeam = teams.find((t) => t.id === game.topTeamId);
        if (!topTeam) {
            return;
        }

        // Empty for round 0 (fixed pairings); also powers "Change opponent".
        const opponents = (model.eligibleOpponents(round, game.gameIndex) ?? [])
            .map((id) => teams.find((t) => t.id === id))
            .filter(Boolean);

        // serializePicks drops winner-less games, so apply opponent + winner as one mutation.
        let pendingOpponent = null;
        const openPickModal = (bottomTeam, clickedTop) =>
            ModalHelper.openModal(<BountyPickModal />, null, {
                anchorTeam: topTeam,
                opponents,
                topTeam,
                bottomTeam,
                bestOf,
                useSeriesScore: model.useSeriesScore,
                selectedTop: clickedTop,
                onPickOpponent: (opponentId) => {
                    pendingOpponent = opponentId;
                },
                onPickWinner: (winnerId, winnerScore, loserScore) =>
                    applyMutation((m) => {
                        if (pendingOpponent != null) {
                            m.pickOpponent(
                                round,
                                game.gameIndex,
                                pendingOpponent
                            );
                        }
                        m.pickWinner(
                            round,
                            game.gameIndex,
                            winnerId,
                            model.useSeriesScore && bestOf > 1
                                ? winnerScore
                                : null,
                            loserScore
                        );
                    }),
            });

        if (game.bottomTeamId == null) {
            if (opponents.length === 0) {
                return;
            }
            openPickModal(null, true);
            return;
        }

        const bottomTeam = teams.find((t) => t.id === game.bottomTeamId);
        if (!bottomTeam) {
            return;
        }

        // Round 0 pairings are fixed, so with no score to predict a click is the winner pick.
        if (round === 0 && (!model.useSeriesScore || bestOf <= 1)) {
            const winnerId = selectedTop ? game.topTeamId : game.bottomTeamId;
            applyMutation((m) =>
                m.pickWinner(round, game.gameIndex, winnerId, null, null)
            );
            return;
        }

        openPickModal(bottomTeam, selectedTop);
    };

    const onSubmit = () => {
        EventHelper.submitPicks(currentGame).then(() => {
            Store.set(StoreKeys.EVENT.IS_EDITING, false);
        });
    };

    return (
        <>
            <div {...surfaceProps}>
                {ready && (
                    <BountyBracket
                        parts={parts}
                        svgWrapper={svgWrapper}
                        hideSeriesScore={!model.useSeriesScore}
                        onPartyClick={clickParty}
                    />
                )}
            </div>
            <BracketActionRow width={viewerWidth}>
                <NameplateButton
                    onClick={onSubmit}
                    disabled={!model.isComplete()}
                    buttonText={"SUBMIT"}
                    nameplateId={32}
                />
            </BracketActionRow>
        </>
    );
}

export default function BountyBracketEditor(props) {
    return connect(<BountyBracketEditorC />, propKeys, props);
}
